import useIsSmall from "@/hooks/useIsSmall";
import { AppointmentStatus, CustomerDto, MerchantUserDto } from "@/models/club";
import { Badge, Box, Button, Drawer, Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAdjustmentsHorizontal, IconFilter, IconSearch, IconX } from "@tabler/icons-react";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { KeyboardEvent, MouseEvent } from "react";
import SearchCustomer from "./SearchCustomer";
import SelectTeam from "./SelectTeam";

type FilterKey = "date" | "customer" | "team" | "status" | "settlement" | "snappPay";
type AppliedFilters = {
  values: DateObject[];
  status: string | null;
  settlementStatus: string | null;
  customer: CustomerDto | null;
  team: MerchantUserDto | null;
  snappPayTransactionId: string;
};
type Props = {
  values: DateObject[]; setValues: (values: DateObject[]) => void;
  status: string | null; setStatus: (status: string | null) => void;
  settlementStatus: string | null; setSettlementStatus: (status: string | null) => void;
  selectedCustomer: CustomerDto | null; setSelectedCustomer: (customer: CustomerDto | null) => void;
  selectedTeam: MerchantUserDto | null; setSelectedTeam: (team: MerchantUserDto | null) => void;
  snappPayTransactionId: string; setSnappPayTransactionId: (id: string) => void;
  appliedFilters: AppliedFilters;
  onRemoveFilter: (key: FilterKey) => void;
  onFilter?: () => void;
};

const statusOptions = [
  { label: "رزرو شده", value: AppointmentStatus.Value0.toString() },
  { label: "نهایی شده", value: AppointmentStatus.Value1.toString() },
  { label: "لغو شده", value: AppointmentStatus.Value2.toString() },
  { label: "پرداخت ناموفق", value: AppointmentStatus.Value3.toString() },
  { label: "بازگشت داده شده", value: AppointmentStatus.Value4.toString() },
];
const settlementOptions = [{ label: "انجام شده", value: "true" }, { label: "انجام نشده", value: "false" }];
const fullName = (person: CustomerDto | MerchantUserDto | null) => person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "";

export default function FilterHeader(props: Props) {
  const isSmall = useIsSmall();
  const [drawerOpened, drawer] = useDisclosure(false);
  const [customerOpened, customerModal] = useDisclosure(false);
  const [teamOpened, teamModal] = useDisclosure(false);
  const apply = () => { props.onFilter?.(); drawer.close(); };
  const active = [
    props.appliedFilters.snappPayTransactionId ? { key: "snappPay" as const, label: `شناسه اسنپ‌پی: ${props.appliedFilters.snappPayTransactionId}` } : null,
    props.appliedFilters.values.length && !props.appliedFilters.snappPayTransactionId ? { key: "date" as const, label: "بازه تاریخ" } : null,
    props.appliedFilters.customer ? { key: "customer" as const, label: `مشتری: ${fullName(props.appliedFilters.customer)}` } : null,
    props.appliedFilters.team ? { key: "team" as const, label: `عضو تیم: ${fullName(props.appliedFilters.team)}` } : null,
    props.appliedFilters.status ? { key: "status" as const, label: statusOptions.find((item) => item.value === props.appliedFilters.status)?.label || "وضعیت نوبت" } : null,
    props.appliedFilters.settlementStatus ? { key: "settlement" as const, label: `تسویه ${props.appliedFilters.settlementStatus === "true" ? "شده" : "نشده"}` } : null,
  ].filter(Boolean) as { key: FilterKey; label: string }[];

  const applyOnEnter = (event: KeyboardEvent<HTMLInputElement>) => { if (event.key === "Enter") apply(); };

  const fields = <>
    <TextInput label="شناسه اسنپ‌پی" placeholder="شناسه تراکنش" description={isSmall ? "با جستجوی شناسه، بازه تاریخ اعمال نمی‌شود" : undefined} value={props.snappPayTransactionId} onChange={(event) => props.setSnappPayTransactionId(event.currentTarget.value)} onKeyDown={applyOnEnter} leftSection={<IconSearch size={15} />} rightSection={props.snappPayTransactionId && <IconX size={15} style={{ cursor: "pointer" }} onClick={() => props.setSnappPayTransactionId("")} />} />
    <TextInput label="مشتری" placeholder="انتخاب مشتری" value={fullName(props.selectedCustomer)} readOnly onClick={customerModal.open} rightSection={props.selectedCustomer && <IconX size={15} onClick={(event: MouseEvent<SVGSVGElement>) => { event.stopPropagation(); props.setSelectedCustomer(null); }} />} />
    <TextInput label="عضو تیم" placeholder="انتخاب عضو تیم" value={fullName(props.selectedTeam)} readOnly onClick={teamModal.open} rightSection={props.selectedTeam && <IconX size={15} onClick={(event: MouseEvent<SVGSVGElement>) => { event.stopPropagation(); props.setSelectedTeam(null); }} />} />
    <DatePicker calendar={persian} locale={persianFa} range value={props.values} onChange={props.setValues} render={<TextInput label="تاریخ" placeholder="انتخاب بازه" readOnly />} />
    <Select label="وضعیت نوبت" placeholder="همه وضعیت‌ها" value={props.status} onChange={props.setStatus} clearable data={statusOptions} />
    <Select label="وضعیت تسویه" placeholder="همه" value={props.settlementStatus} onChange={props.setSettlementStatus} clearable data={settlementOptions} />
  </>;

  return <>
    <Box>
      {isSmall ? <Button fullWidth variant="light" leftSection={<IconAdjustmentsHorizontal size={18} />} onClick={drawer.open}>فیلتر نتایج{active.length ? ` (${active.length.toLocaleString("fa-IR")})` : ""}</Button>
        : <Group align="flex-end" gap="xs" wrap="nowrap"><Box style={{ display: "contents" }}>{fields}</Box><Button miw={112} leftSection={<IconFilter size={16} />} onClick={apply}>اعمال فیلتر</Button></Group>}
      {active.length > 0 && <Group gap={6} mt="xs">{active.map((item) => <Badge key={item.key} variant="light" size="lg" rightSection={<IconX size={12} style={{ cursor: "pointer" }} onClick={() => props.onRemoveFilter(item.key)} />}>{item.label}</Badge>)}</Group>}
    </Box>

    <Drawer opened={drawerOpened} onClose={drawer.close} position="bottom" size="auto" radius="lg" title={<Group gap="xs"><IconAdjustmentsHorizontal size={19} /><Text fw={700}>فیلتر نوبت‌ها</Text></Group>}>
      <Stack gap="sm" pb="md">{fields}<Button mt="xs" leftSection={<IconFilter size={17} />} onClick={apply}>اعمال فیلتر و نمایش نتایج</Button></Stack>
    </Drawer>
    <Modal opened={customerOpened} onClose={customerModal.close} title="انتخاب مشتری"><SearchCustomer setSelectedCustomer={props.setSelectedCustomer} close={customerModal.close} /></Modal>
    <Modal opened={teamOpened} onClose={teamModal.close} title="انتخاب عضو تیم"><SelectTeam setSelectedTeam={props.setSelectedTeam} close={teamModal.close} /></Modal>
  </>;
}
